import React from 'react';
import { Helmet } from 'react-helmet';
import { StickyContainer, Sticky } from 'react-sticky';
import qs from 'qs';

import { withVariants } from 'inside-out/managers/componentManager';
import { idToLink } from 'inside-out/managers/navManager';
import { typeToDescription } from 'inside-out/managers/treatmentServicesManager';
import { FilterManager } from 'inside-out/managers/filterManager';

import { FilterDropDown } from 'inside-out/components/shared/filterDropDown';
import { Cards } from 'inside-out/components/shared/cards';
import { ColouredCard } from 'inside-out/components/shared/cards/colouredCard';
import { TreatmentServiceCard } from 'inside-out/components/shared/cards/treatmentServiceCard';
import { PageHeader } from 'inside-out/components/shared/pageHeader';
import { PageNav } from 'inside-out/components/shared/pageNav';
import { navigateTo } from 'inside-out/components/shared/link';

import { MapElement } from 'inside-out/components/treatment-services/map';
import {
	SearchFilter,
	TypesFilter,
	DisciplineFilters,
	AgeRangeFilters,
	ServiceFilters,
} from 'inside-out/components/treatment-services/filters';

import TreatmentServicesBG from 'inside-out/images/treatment-services-database/bg.svg';

import {
	innerWrapperStyle,
	filtersContainerStyle,
	filtersInnerWrapperStyle,
	searchFieldStyle,
	dropdownStyle,
	leftStyle,
	rightStyle,
	mobileFiltersStyle,
	filtersWrapperStyle,
	filtersRowStyle,
	pageNavsStyle,
} from 'inside-out/page-styles/treatment-services-database';

import { purple, pink, tabletPortraitMaxWidthBreakpoint } from 'inside-out/global-styles';

const ColouredCardVariant = withVariants(ColouredCard, {
	[`max-width: ${tabletPortraitMaxWidthBreakpoint}px`]: 'minimized',
});

// This page is not bound to data from Gatsby, it uses REST to talk to a search
// function, which is a Netlify function in the /netlify directory.

const defaultFilters = { query: '', types: '', disciplines: '', ages: '', services: '' };

export default class TreatmentServicesSearchResultsPage extends React.PureComponent {
	state = {
		loading: true,
		isSearchng: true,
		error: null,
		mode: 'list',
		searchResults: [],
		filters: {
			query: '',
			types: '',
		},
		bounds: null,
	};
  blockSearch = false;
	dropdowns = {};

	constructor(props, context) {
		super(props, context);

		// There's a bug in babel with async arrow functions, so bind in constructor until
		// it's fixed.
		this.search = this.search.bind(this);
	}

	componentDidMount() {
		this.unlisten = this.props.history.listen(this.didChangeRoute);
		// Kick off our initial search
    const params = qs.parse(location.search, { ignoreQueryPrefix: true });
    this.setState(prevState => ({
    	...prevState,
    	filters: {
    		...defaultFilters,
				...params
    	}
    }), () => {
	    this.search();
    });
	}

	componentWillUnmount() {
		this.unlisten();
	}

	didChangeRoute = () => {
		if (!this.blockSearch) {
			this.setState({
				filters: {
					...defaultFilters
				}
			}, () => {
				this.search();
			});
		}
	}

	updateFilters = (filter, search = false) => {
		this.setState(prevState => {
			let filters = {
				...prevState.filters,
				...filter
			};
			return {
				...prevState,
				filters
			};
		}, () => {
			if (search) this.search();
		});
	}

  async search() {
		try {
			this.blockSearch = true;
			const base = location.hostname === 'localhost'
				? 'http://localhost:9000'
				: '/.netlify/functions';

			const filters = this.state.filters;
			const params = Object.keys(this.state.filters)
				.filter(k => !!filters[k])
				.reduce((acc, k) => Object.assign(acc, { [k] : filters[k] }), {});
			const queryParams = qs.stringify(params);

			// update the query string
			navigateTo(`/treatment-services?${ queryParams }`);

			// request data
			const results = await fetch(`${base}/search-treatment-services?${ queryParams }`);
			const parsed = await results.json();

			const searchResults = parsed.map(node => ({
				to: idToLink(node.id),
				image: node.fields && node.fields.avatar_image.childImageSharp.resize.src,
				location: node.state,
				title: node.title,
				subTitle: node.discipline,
				attributes: [
					{
						title: typeToDescription(node.type),
					},
				],
				latitude: node.latitude,
				longitude: node.longitude,
			}));

			const markersData = searchResults.map(service => ({
				position: {
					lat: service.latitude,
					lng: service.longitude,
				},
				cardData: service,
			}));

			this.setState({ loading: false, searchResults, markersData });
			this.blockSearch = false;
		} catch (error) {
			console.warn(error);
			this.blockSearch = false;
		}
	}

	render() {
		const { loading, error, filters, searchResults, markersData } = this.state;

		return (
			<React.Fragment>
				<Helmet title="Treatment Services Database">
          <meta property="og:type" content="website"/>
          <meta property="og:title" content="Treatment Services Database"/>
          <meta name="description" content="InsideOut's Treatment Services Database can assist you in finding a private practitioner, community clinic or program, day program, in hospital treatment, support group or clinical supervisor near you."/>
          <meta property="og:description" content="InsideOut's Treatment Services Database can assist you in finding a private practitioner, community clinic or program, day program, in hospital treatment, support group or clinical supervisor near you."/>
        </Helmet>

				<div className={pageNavsStyle}>
					<PageHeader
						background={`${purple} url(${TreatmentServicesBG}) no-repeat bottom right`}
						titleText={'Treatment Services Database'}
						titleTextColor="#fff"
						subTitleText={
							"InsideOut's Treatment Services Database can assist you in finding a private practitioner, community clinic or program, day program, in hospital treatment, support group or clinical supervisor near you."
						}
						subTitleTextColor="#fff"
					/>

					<PageHeader
						background={`${purple} url(${TreatmentServicesBG}) no-repeat bottom right`}
						titleText={'Treatment Services Database'}
						titleTextColor="#fff"
						pageNav={
							<PageNav
								textColor="#fff"
								activeTextColor="#fff"
								activeUnderlineColor="#fff"
								managed={{
									filterManager: FilterManager,
									onClick: (key, e) => {
										e.preventDefault();
										this.setState({ mode: key });
										// BUG: Issue with setting updated state in component
										// Shouldn't need a filter manager, let alone in a setTimeout
										setTimeout(() => FilterManager.hideAllFilters(), 0);
									},
									activeLink: this.state.mode,
								}}
								links={[
									{
										to: '?mode=list',
										managedKey: 'list',
										text: 'List',
									},
									{
										to: '?mode=map',
										managedKey: 'map',
										text: 'Map',
									},
								]}
							/>
						}
					/>
				</div>

				<div className={ mobileFiltersStyle }>
					<div className={ filtersWrapperStyle }>
						<div className={ searchFieldStyle }>
							<SearchFilter
								value={ filters.query }
								onChange={ query => this.updateFilters({ query }) }
								onSubmit={ () => this.search() }/>
						</div>

						<FilterDropDown
							componentName="Service Type"
							anchorTo={`.${ filtersWrapperStyle }`}
							className={ dropdownStyle }
							hasBadge={ !!filters.types }
							ref={ref => (this.dropdowns.serviceFilter = ref) }>
							<div className={ filtersContainerStyle }>
								<div className={ filtersInnerWrapperStyle }>
									<TypesFilter
										value={ filters.types || '' }
										onChange={ types => this.updateFilters({ types }, true) }/>
								</div>
							</div>
						</FilterDropDown>

						<FilterDropDown
							componentName="Other Filters"
							anchorTo={`.${ filtersWrapperStyle }`}
							className={ dropdownStyle }
							hasBadge={ !!filters.disciplines || !!filters.ages || !!filters.services }
							ref={ref => (this.dropdowns.otherFilters = ref) }>
							<div className={ filtersContainerStyle }>
								<div className={ filtersInnerWrapperStyle }>
									<h3>Health Professional Discipline</h3>
									<DisciplineFilters
										value={ filters.disciplines || '' }
										onChange={ disciplines => this.updateFilters({ disciplines }, true) }/>
									<div>
									<div className={ filtersRowStyle }>
										<AgeRangeFilters
											value={ filters.ages || '' }
											onChange={ ages => this.updateFilters({ ages }, true) }/>
										<ServiceFilters
											value={ filters.services || '' }
											onChange={ services => this.updateFilters({ services }, true) }/>
										</div>
									</div>
								</div>
							</div>
						</FilterDropDown>
					</div>
				</div>

				<StickyContainer>
					<div className={innerWrapperStyle(this.state.mapIsToggled)}>
						{ error && (
							<p>{ JSON.stringify(error) }</p>
						) }
						{ loading && (
							<p>Loading...</p>
						) }
						{ !loading && (
							<React.Fragment>
								<div className={leftStyle}>
									<Cards
										scrollable={false}
										columns={2}
										columnsAt={{ [`max-width: ${tabletPortraitMaxWidthBreakpoint}`]: 1 }}>

										<ColouredCardVariant
											id={0}
											background={pink}
											title="Get listed"
											href="https://cedd.typeform.com/to/eJjefF"
											body="Health Professionals who wish to be added, please click here to add your details via a short survey."
											arrow={true}/>
										{searchResults.map((serviceData, index) => {
											return <TreatmentServiceCard key={index} {...serviceData} />;
										})}
									</Cards>
								</div>
								<div className={rightStyle}>
									{this.state.mode !== 'map' && (
										<Sticky>
											{ ({
												style,
												isSticky,
												wasSticky,
												//distanceFromTop,
												//distanceFromBottom,
												//calculatedHeight,
											}) => {
												if (style && (isSticky || wasSticky)) {
													style.marginTop = '20px';
												}
												return <MapElement style={ style } data={ markersData }/>;
											} }
										</Sticky>
									) }
									{ this.state.mode === 'map' && (
										<MapElement/>
									) }
								</div>
							</React.Fragment>
						) }
					</div>
				</StickyContainer>
			</React.Fragment>
		);
	}
}



// WEBPACK FOOTER //
// ./src/pages/treatment-services/index.js